import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ConnectionsList from '../components/ConnectionsList';
import ShareModal from '../components/ShareModal';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, UserCheck, Clock, X } from 'lucide-react';
import { 
  sendInvitation, 
  getReceivedInvitations, 
  getSentInvitations,
  acceptInvitation,
  declineInvitation,
  cancelInvitation
} from '../services/invitationService';
import { collection, query, where, getDocs, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAccessibleGroups, shareGroup, shareCard } from '../services/sharingService';
import { toast } from 'react-hot-toast';

const getToUsers = (toUserIdData) => {
  if (!toUserIdData) return [];
  
  // Handle the nested users structure
  if (toUserIdData.users) {
    const usersArray = toUserIdData.users;
    
    // If the first item is an object with a nested users array
    if (usersArray[0]?.users) {
      return usersArray.flatMap(userObj => userObj.users || [])
        .filter(id => id !== null);
    }
    
    // If it's a simple array of user IDs
    return usersArray.filter(id => id !== null);
  }
  
  return [];
};

const ConnectPage = () => {
  console.log('ConnectPage rendering');

  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  console.log('currentUser:', currentUser, 'authLoading:', authLoading);

  const [email, setEmail] = useState('');
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [cards, setCards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [cardSharedGroups, setCardSharedGroups] = useState({});

  // Handle auth redirect - only redirect if we're sure auth is done loading
  useEffect(() => {
    if (!authLoading && !currentUser) {
      console.log('Auth loaded, no user found - redirecting');
      navigate('/');
    }
  }, [currentUser, authLoading, navigate]);

  // Handle invitations loading
  useEffect(() => {
    const loadInvitations = async () => {
      if (!currentUser?.uid) return;
      
      try {
        setIsLoading(true);
        const [received, sent] = await Promise.all([
          getReceivedInvitations(currentUser.uid),
          getSentInvitations(currentUser.uid)
        ]);
        console.log('Loaded invitations:', { received, sent });
        setReceivedInvitations(received);
        setSentInvitations(sent);
      } catch (err) {
        console.error('Error loading invitations:', err);
        setError('Failed to load invitations. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadInvitations();
    }
  }, [currentUser]);

  // Update the fetchCardsAndGroups function
  useEffect(() => {
    const fetchCardsAndGroups = async () => {
      if (!currentUser?.uid) return;
      
      try {
        // Get all groups the user can access
        const accessibleGroups = await getAccessibleGroups(currentUser.uid);
        setGroups(accessibleGroups.map(g =>
          typeof g === 'string' ? { id: g, name: g } : g
        ));

        // Fetch all cards from accessible groups
        const cardsRef = collection(db, 'cards');
        const cardsPromises = accessibleGroups.map(async groupName => {
          const groupCardsQuery = query(cardsRef, where('group', '==', groupName));
          const snapshot = await getDocs(groupCardsQuery);
          return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            isShared: doc.data().userId !== currentUser.uid
          }));
        });

        const allCards = await Promise.all(cardsPromises);
        setCards(allCards.flat());
      } catch (error) {
        console.error('Error fetching cards and groups:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCardsAndGroups();
  }, [currentUser]);

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  // Only redirect if we're sure auth is done loading
  if (!authLoading && !currentUser) {
    return null;
  }

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      const invitation = await sendInvitation(currentUser, email);
      setSentInvitations(prev => [...prev, invitation]);
      setEmail('');
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError('Failed to send invitation. Please try again.');
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await acceptInvitation(invitationId);
      setReceivedInvitations(prev => 
        prev.filter(inv => inv.id !== invitationId)
      );
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation. Please try again.');
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await declineInvitation(invitationId);
      // Refresh the invitations list after declining
      const [received, sent] = await Promise.all([
        getReceivedInvitations(currentUser.uid),
        getSentInvitations(currentUser.uid)
      ]);
      setReceivedInvitations(received);
      setSentInvitations(sent);
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError('Failed to decline invitation. Please try again.');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelInvitation(invitationId);
      // Refresh the invitations list after canceling
      const [received, sent] = await Promise.all([
        getReceivedInvitations(currentUser.uid),
        getSentInvitations(currentUser.uid)
      ]);
      setReceivedInvitations(received);
      setSentInvitations(sent);
    } catch (err) {
      console.error('Error canceling invitation:', err);
      setError('Failed to cancel invitation. Please try again.');
    }
  };

  const handleShare = async (recipientId, group, withEditAccess = false) => {
    try {
      await shareGroup(currentUser.uid, recipientId, group, withEditAccess);
      toast.success('Group shared successfully!');
    } catch (error) {
      console.error('Error sharing group:', error);
      toast.error(error.message || 'Failed to share group');
    }
  };

  const handleShareSubmit = async (recipientId, card, withEditAccess = false) => {
    try {
      if (!currentUser || !recipientId || !card) {
        throw new Error('Missing required data for sharing');
      }
      
      await shareCard(currentUser.uid, recipientId, card, withEditAccess);
      setShowShareModal(false);
      toast.success('Card shared successfully!');
    } catch (error) {
      console.error('Error sharing card:', error);
      toast.error(error.message || 'Failed to share card');
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={currentUser} />
      {console.log('Rendering main content')}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-8">
          <ConnectionsList 
            currentUser={currentUser}
            cards={cards}
            groups={groups}
            onShare={handleShare}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Received Invitations Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#04b53a]" />
              Received Invitations
            </h2>
            {isLoading ? (
              <p className="text-center py-4">Loading...</p>
            ) : receivedInvitations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No pending invitations
              </p>
            ) : (
              <ul className="space-y-4">
                {receivedInvitations.map((invitation) => (
                  <li key={invitation.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{invitation.fromUserName}</p>
                      <p className="text-sm text-gray-500">{invitation.fromUserEmail}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptInvitation(invitation.id)}
                        className="px-3 py-1 bg-[#04b53a] text-white rounded hover:bg-[#039431] transition-colors"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                      >
                        Decline
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Send Invitations Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#04b53a]" />
              Send Invitations
            </h2>
            <form onSubmit={handleSendInvitation} className="mb-6">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="flex-1 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#04b53a]"
                  required
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#04b53a] text-white rounded hover:bg-[#039431] transition-colors"
                >
                  Send
                </button>
              </div>
            </form>

            <h3 className="text-lg font-medium text-gray-900 mb-3">Pending Invitations</h3>
            {isLoading ? (
              <p className="text-center py-4">Loading...</p>
            ) : sentInvitations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No pending invitations
              </p>
            ) : (
              <ul className="space-y-4">
                {sentInvitations.map((invitation) => (
                  <li key={invitation.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">To: {invitation.toEmail}</p>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                        <Clock className="w-4 h-4" />
                        <span>Pending</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                      title="Cancel invitation"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onShare={handleShareSubmit}
          currentUser={currentUser}
          recipient={selectedRecipient}
          cards={cards}
          groups={groups}
        />
      </div>
    </div>
  );
};

export default ConnectPage; 